import { useCallback, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { toastManager } from '@systemeio/components'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import type { LocaleEnum } from 'shared/enums/locale-enum'
import useUser from 'shared/hooks/use-user'
import { changeLocale } from 'shared/i18n/change-locale'

interface UseLanguageSwitcherResult {
  handleChangeLanguage: (
    locale: LocaleEnum,
    options?: {
      forceRouterUpdate?: boolean
    },
  ) => Promise<void>
  isChangingLanguage: boolean
}

export const useLanguageSwitcher = (): UseLanguageSwitcherResult => {
  const { i18n, t } = useTranslation()
  const router = useRouter()
  const { user, mutate } = useUser()
  const [isChangingLanguage, setIsChangingLanguage] = useState(false)

  const handleChangeLanguage = useCallback(
    async (locale: LocaleEnum, options?: { forceRouterUpdate?: boolean }) => {
      try {
        setIsChangingLanguage(true)
        await changeLocale({
          i18n,
          locale,
          disableBackendUpdate: !user,
          userDashboardLocale: user?.dashboardLocale,
          mutateUser: mutate,
          revalidateUser: true,
          router,
          forceRouterUpdate: options?.forceRouterUpdate,
        })
      } catch (error) {
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
          extra: {
            context: 'Failed to change language',
          },
        })
        toastManager.add({ type: 'error', title: t('errors.language_change_failed') })
      } finally {
        setIsChangingLanguage(false)
      }
    },
    [i18n, mutate, router, t, user],
  )
  return { handleChangeLanguage, isChangingLanguage }
}
